<script setup lang="ts">
import 'bootstrap';

import $ from 'jquery';
import {
    computed,
    inject, nextTick, onBeforeUnmount, onMounted, provide, ref, shallowRef, unref, watch,
} from 'vue';

import { errutil } from '#public/commons-global';
import { type NoShowError, noShowError } from '#public_ts/commons/fe-commons';

import { useGenerateIds, useJqueryEventListener } from '../composables';
import { ModalCancelBehavior, type ModalDialogApi } from './bootstrap-modal';
import type { TitleType } from './bootstrap-panel';
import { PortalPin } from './injection';

//modelValue + update:modelValue;
type ModalSize = 'modal-sm' | 'modal-lg' | 'modal-xxl' | 'modal-fullscreen' | 'modal-fullscreen-no-padding';
//valueProp, it cannot handle dynamic ones
const props = defineProps<{
    title?: string | boolean;
    titleType?: TitleType;
    ok?: string | boolean;
    oktype?: 'submit' | 'button';
    cancel?: string | boolean;
    close?: boolean;
    headerClose?: boolean;
    size?: ModalSize;
    static?: boolean;
    disabled?: boolean;
    modelValue?: boolean;
    pin?: boolean;
    modalClass?: string;
    formClass?: string;
    bodyClass?: string;
    parentForm?: boolean;
    hideFooter?: boolean;
}>();
//eslint-obsolete no-spaced-func
const emit = defineEmits<{
    (event: 'update:modelValue', value: boolean | undefined): void;
    (event: 'form-submit', value: undefined): void;
    (event: 'dialog-opened', value: undefined): void;
    (event: 'dialog-closed', value: undefined): void;
    (event: 'after-submit', value: undefined): void;
    (event: 'after-cancel', value: undefined): void;
}>();

const generateIds = useGenerateIds();
const labelId = generateIds();
const showModal = ref<boolean>(false);
let modalVisible = false;
const transition = ref(false);
let onBeforeSubmit: (() => Promise<boolean>) | undefined;
const modalSubmitted = ref(false);
let modalDepth = 0;
let modalSessionId = '';
let historyPushed = false;
const modalElement = shallowRef<HTMLElement | undefined>();
const hasForm = ref(true);
const hasOk = computed(() => !((props.ok === false) || (props.ok === undefined)));
const hasCancel = computed(() => !((props.cancel === false) || (props.cancel === undefined)));
const portalPin = inject(PortalPin, undefined);
if(portalPin !== undefined) {
    //this fix for modals inside modals, so they all go to separate portal
    provide(PortalPin, undefined);
}

const modalApi: ModalDialogApi = {
    toggleModal(_showModal) {
        return Promise.resolve()
            .then(() => updateShowModal(_showModal))
            .then(() => waitCurrentTransition())
            .then(() => { /**/ });
    },
    requestSubmit,
    isSubmitted: computed(() => unref(modalSubmitted)),
    showModal(options) {
        if(onBeforeSubmit) return Promise.reject(new Error('Already waiting for dialog'));
        onBeforeSubmit = () => Promise.resolve()
            .then(() => {
                const _onBeforeSubmit = options?.onBeforeSubmit;
                return _onBeforeSubmit?.();
            })
            .then(() => {
                updateShowModal(false);
                return true;
            })
            .catch((err: NoShowError) => {
                if(err && !err.no_show_error) errutil.box(null, err);
                return false;
            });

        return new Promise((resolve, reject) => {
            const _unwatch = watch(showModal, () => {
                Promise.resolve()
                    .then(() => waitCurrentTransition())
                    .then(() => {
                        if(unref(showModal)) return;
                        _unwatch();
                        onBeforeSubmit = undefined;
                        if(unref(modalSubmitted)) {
                            resolve();
                            return;
                        }
                        const onCancel = options?.onCancel ?? ModalCancelBehavior.NoReturn;
                        switch(onCancel) {
                            case ModalCancelBehavior.Resolve:
                                resolve(undefined);
                                break;
                            case ModalCancelBehavior.Reject:
                                reject(noShowError('Cancelled'));
                                break;
                        }
                    });
            });
            updateShowModal(true);
        });
    },
};
defineExpose(modalApi);

function waitCurrentTransition() {
    return new Promise((resolve) => {
        if(!unref(transition)) {
            resolve(undefined);
            return;
        }
        const _unwatch = watch(transition, () => {
            // multi-entry support, transition may be modified by other waiter
            // then we wait another transition
            if(unref(transition)) return;
            _unwatch();
            resolve(undefined);
        });
    });
}

function updateShowModal(_showModal: boolean | undefined) {
    const _showModal0 = !!_showModal;
    const _modalElement = unref(modalElement);
    if(!_modalElement) return Promise.resolve();

    return Promise.resolve()
        .then(() => waitCurrentTransition())
        .then(() => {
            if(_showModal0 === unref(showModal)) return;

            transition.value = true;
            showModal.value = _showModal0;
            if(!_showModal0) {
                $(_modalElement).modal('hide');
            } else {
                modalSubmitted.value = false;
                $(_modalElement).modal('show');
            }
        });
}

function currentHistoryDepth() {
    const state = history.state as { bootstrapModalDepth?: number } | null;
    return typeof state?.bootstrapModalDepth === 'number' ? state.bootstrapModalDepth : 0;
}

function currentHistorySession() {
    const state = history.state as { bootstrapModalSession?: string } | null;
    return typeof state?.bootstrapModalSession === 'string' ? state.bootstrapModalSession : undefined;
}

function generateModalSessionId() {
    return `fe-modal-${Date.now().toString(36)}-${Math.random().toString(36).slice(2, 10)}`;
}

function pushModalHistoryState() {
    if(historyPushed) return;
    historyPushed = true;
    modalDepth = currentHistoryDepth() + 1;
    modalSessionId = currentHistorySession() ?? generateModalSessionId();
    history.pushState({
        ...(history.state ?? {}),
        bootstrapModalDepth: modalDepth,
        bootstrapModalSession: modalSessionId,
    }, '');
}

function popModalHistoryState() {
    if(!historyPushed) return;
    historyPushed = false;
    history.back();
}

function onPopState() {
    if(!historyPushed) return;
    if(currentHistoryDepth() >= modalDepth) return;
    const currentSession = currentHistorySession();
    if(currentSession !== undefined && currentSession !== modalSessionId) return;
    historyPushed = false;
    if(unref(showModal)) {
        updateShowModal(false);
    }
}

function requestSubmit() {
    Promise.resolve()
        .then(() => onBeforeSubmit?.())
        .then((submitResult) => {
            if(submitResult === false) return;
            modalSubmitted.value = true;
            emit('form-submit', undefined);
        });
}

function tyrRequestSubmit() {
    if(!props.parentForm) {
        requestSubmit();
    }
    // form will submit
}

function processModalToggle(_showModal: boolean | undefined) {
    const _showModal0 = !!_showModal;
    nextTick(() => {
        if(_showModal0 !== unref(showModal)) {
            // warning, properties not in sync, see updateShowModal
            showModal.value = _showModal0;
        }
        if(!!props.modelValue !== _showModal0) {
            emit('update:modelValue', _showModal0);
        }
        transition.value = false;
        if(_showModal0) {
            emit('dialog-opened', undefined);
        } else {
            emit('dialog-closed', undefined);
            if(unref(modalSubmitted)) {
                emit('after-submit', undefined);
            } else {
                emit('after-cancel', undefined);
            }
        }
    });
}

watch(() => props.modelValue, (val, old) => {
    if(!!val !== !!old) {
        updateShowModal(val);
    }
});

onMounted(() => {
    window.addEventListener('popstate', onPopState);
    //portal requires next tick
    nextTick(() => {
        const _modalElement = unref(modalElement);
        if(_modalElement) {
            hasForm.value = $(_modalElement).closest('form').length > 0;
        }

        if(props.modelValue) {
            updateShowModal(true);
        }
    });
});
onBeforeUnmount(() => {
    window.removeEventListener('popstate', onPopState);
    const _modalElement = unref(modalElement);
    if(_modalElement && modalVisible) {
        // before removing hide it without animation
        $(_modalElement).removeClass('fade').modal('hide');
    }
});

useJqueryEventListener(modalElement, 'show.bs.modal', () => {
    modalVisible = true;
});
useJqueryEventListener(modalElement, 'shown.bs.modal', () => {
    modalSubmitted.value = false;
    processModalToggle(true);
    pushModalHistoryState();
});
useJqueryEventListener(modalElement, 'hide.bs.modal', () => {
    transition.value = true;
});
useJqueryEventListener(modalElement, 'hidden.bs.modal', () => {
    //model value is updated at this point
    modalVisible = false;
    processModalToggle(false);
    popModalHistoryState();
});
</script>
<script lang="ts">
export default {
    inheritAttrs: false,
};
</script>
<template>
    <Teleport to="#portal-target" :disabled="props.pin || portalPin">
        <div
            v-bind="$attrs"
            ref="modalElement"
            class="modal fade"
            :class="props.modalClass"
            tabindex="-1"
            role="dialog"
            :aria-labelledby="labelId"
            aria-hidden="true"
            :data-backdrop="props.static ? 'static' : 'true'"
            :data-keyboard="props.static ? undefined : true"
        >
            <div
                class="modal-dialog"
                :class="props.size === 'modal-fullscreen-no-padding' ? 'modal-fullscreen modal-fullscreen-no-padding' : props.size"
                role="document"
            >
                <div class="modal-content" :class="props.formClass ?? (hasForm ? '' : 'form-horizontal')">
                    <slot name="modalcontentpre" />
                    <div class="modal-header" :data-dismiss="props.headerClose ? 'modal' : undefined">
                        <slot name="header">
                            <button
                                v-if="!((props.close === false) || (props.close === undefined))"
                                type="button"
                                class="close"
                                data-dismiss="modal"
                                aria-label="Chiudi"
                            >
                                <span aria-hidden="true">&times;</span>
                            </button>
                            <Component
                                :is="props.titleType || 'h4'"
                                v-if="!((props.title === false) || (props.title === undefined))"
                                :id="labelId"
                                class="modal-title"
                            >
                                <slot name="title">
                                    {{ props.title }}
                                </slot>
                            </Component>
                        </slot>
                    </div>
                    <div class="modal-body bg-body-secondary" :class="props.bodyClass">
                        <slot />
                    </div>
                    <div v-if="!props.hideFooter" class="modal-footer">
                        <slot name="footer">
                            <button
                                v-if="hasCancel"
                                type="button"
                                class="btn btn-inverse"
                                data-dismiss="modal"
                            >
                                {{ ((typeof(props.cancel) === 'string') && props.cancel) || (hasOk ? 'Annulla' : 'Chiudi') }}
                            </button>
                            <button
                                v-if="hasOk"
                                :type="props.oktype || 'submit'"
                                class="btn btn-primary"
                                :disabled="props.disabled"
                                @click="tyrRequestSubmit"
                            >
                                {{ ((typeof(props.ok) === 'string') && props.ok) || 'Ok' }}
                            </button>
                        </slot>
                    </div>
                    <slot name="modalcontentpost" />
                </div>
            </div>
        </div>
    </Teleport>
</template>
<style>
.modal-fullscreen .modal-content {
    height: 100%;
    display: flex;
    flex-direction: column;
}

.modal-fullscreen .modal-header {
    flex-shrink: 0;
}

.modal-fullscreen .modal-footer {
    flex-shrink: 0;
}

.modal-fullscreen .modal-body {
    flex: 1;
    overflow-y: auto;
    box-sizing: border-box;
    width: 100%;
}

/* Desktop only */
body.menu-layout-desktop .modal-fullscreen {
    width: 90%;
    height: 90%;
}

/* Mobile in any orientation */
body.menu-layout-mobile .modal-fullscreen {
    width: 100%;
    max-width: none;
    height: 100%;
    margin: 0;
}

body.menu-layout-mobile .modal-fullscreen .modal-content {
    box-shadow: none;
    border-radius: 0;
    border: none;
}

body.menu-layout-mobile .modal-fullscreen .modal-body {
    padding: 10px;
}

body.menu-layout-mobile .modal-fullscreen-no-padding .modal-body {
    padding-left: 0px;
    padding-right: 0px;
}

</style>
